import { isSupportedLanguage, localeFor } from "@/lib/i18n/languages";

/**
 * Numeric date display, always zero-padded (03.09.2026 / 03/09/2026), never
 * single-digit (3.9.2026). Use wherever a date is shown as digits-only.
 *
 * `language` is any SupportedLanguage (COL-478); unknown or missing keeps the
 * historical en-GB default so untouched callers render exactly as before.
 */
export function formatDateNumeric(
  value: string | number | Date | null | undefined,
  language?: string
): string {
  if (value == null || value === "") return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const locale = isSupportedLanguage(language) ? localeFor(language) : "en-GB";
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * DD.MM.YYYY, in every language (COL-533 design QA, Vivi's item 4).
 *
 * The tenant dashboard used to print each date in its own locale's numeric
 * style, so the same screen showed `26/06/2026` to an English tenant and
 * `26.06.2026` to a German one. Every property is in Germany and the dates are
 * German ones — a move-in, a rent due date, a Bürgeramt deadline — so they are
 * written the German way throughout rather than reformatted per reader.
 *
 * Deliberately NOT locale-derived: passing the reader's language back in is
 * exactly the behaviour this replaces. Built on `formatDateNumeric` so the
 * zero-padding and the invalid/empty handling stay in one place.
 */
export function formatTenantDate(
  value: string | number | Date | null | undefined
): string {
  return formatDateNumeric(value, "de");
}
